/** Everyone's personalities: one row per person, their type and the four traits behind it. */

import { Avatar, Fit, Scene, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { TYPE_NAMES, type Chat } from "../../types";

const BLOBS: BlobSpec[] = [{ colour: "fuchsia", x: 1030, y: 1810, r: 220, seed: 31.2, wob: 0.2 }];

const TOP = 370;
// Down to just above the phone's arrows, on the designed 1920 stage.
const ROOM = 1780 - TOP;
const STEP = 236;
const ROW = 206;
// Past this the rows look padded out rather than roomy.
const MOST_GROWTH = 1.3;

/**
 * "NIGHT · STARTER · SHORT · MEMES". The sixteen types are listed in trait
 * order: day starters, day repliers, night starters, night repliers, and
 * within each four short words, short memes, long words, long memes.
 */
export function traitsOf(type: string): string | null {
  const at = (TYPE_NAMES as readonly string[]).indexOf(type);
  if (at < 0) return null;
  const traits = [at >= 8 ? "night" : "day", at % 8 >= 4 ? "replier" : "starter", at % 4 >= 2 ? "long" : "short", at % 2 ? "memes" : "words"];
  return traits.join(" · ").toUpperCase();
}

/**
 * How the rows fit: six fit at the video's size and a bigger chat shrinks them
 * all alike. A taller stage's extra height goes to the list: it shrinks less,
 * or its rows grow a little taller and further apart, and whatever is still
 * left goes half above it (`drop`).
 */
export function rowPlan(rows: number, extra = 0) {
  if (!rows) return { scale: 1, grow: 1, drop: 0 };
  const natural = rows * STEP - (STEP - ROW);
  // As tall as the list stands at 1920, plus the extra.
  const goal = Math.min(ROOM, natural) + extra;
  const scale = Math.min(1, goal / natural);
  const grow = Math.min(MOST_GROWTH, Math.max(1, goal / natural));
  return { scale, grow, drop: Math.max(0, Math.round((goal - natural * scale * grow) / 2)) };
}

export function Personalities({ ceremony, me }: { ceremony: Chat["ceremony"]; me: string | null }) {
  const extra = useStageExtra();
  const { scale, grow, drop } = rowPlan(ceremony.length, extra);

  const root = useTimeline((s) => {
    s.rise(".kick, .lead", 0.35, { y: 30, stagger: 0.12 });
    const all = s.q(".prow");
    const step = Math.min(0.16, 1.6 / Math.max(all.length, 1));
    if (all.length) {
      s.tl.fromTo(all, { x: 1000 / scale, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.55, stagger: step, ease: "power4.out" }, 0.55);
      s.sound("whoosh", 0.55, 1, { pan: 0.6, length: 0.55 });
    }
    const mine = s.q(".prow.me");
    if (mine.length) {
      const at = Math.max(2.4, 0.55 + all.length * step + 0.3);
      s.tl.to(mine, { scale: 1.04, duration: 0.3, yoyo: true, repeat: 1, ease: "sine.inOut" }, at);
      s.sound("pop", at, 1.3);
    }
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick">Everyone's personalities</div>
          <div className="lead">The whole chat, one type each</div>
        </Zone>
        {/* The list takes the extra height (see rowPlan); only what it leaves moves it down. */}
        <Zone at={extra ? drop / extra : 0}>
          <div
            className="abs"
            style={{ left: 72, top: TOP, width: 936 / scale, transform: scale < 1 ? `scale(${scale})` : undefined, transformOrigin: "0 0" }}
          >
            {ceremony.map((person, i) => {
              const mine = person.name === me;
              const traits = traitsOf(person.personality);
              return (
                <div
                  key={person.name}
                  data-testid="personality"
                  className={`prow${mine ? " me" : ""}`}
                  style={{ left: 0, top: Math.round(i * STEP * grow), width: "100%", ...(grow > 1 ? { height: Math.round(ROW * grow) } : {}) }}
                >
                  <Avatar name={person.name} inline />
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <div className="who" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {person.name}
                      {mine ? " · YOU" : ""}
                    </div>
                    <Fit size={72} className="disp">
                      {person.personality}
                    </Fit>
                    {traits ? <div className="tr">{traits}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
